import { useState } from "react";
import { Link } from "react-router";
import {
  Bell,
  Calendar,
  CheckCheck,
  Trash2,
  CheckCircle2,
  Info,
  Clock,
} from "lucide-react";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import {
  useNotifications,
  type AppNotification,
} from "../notifications/notificationsStore";
import { cn } from "@/lib/utils";

function formatNotificationTime(timestamp: number): string {
  const diff = Date.now() - timestamp;
  if (diff < 60000) return "Agora mesmo";
  if (diff < 3600000) {
    const min = Math.floor(diff / 60000);
    return `Há ${min} min`;
  }
  if (diff < 86400000) {
    const hours = Math.floor(diff / 3600000);
    return `Há ${hours} h`;
  }
  return new Date(timestamp).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getNotificationIcon(type?: AppNotification["type"]) {
  switch (type) {
    case "calendar":
      return <Calendar className="w-4 h-4 text-blue-500 shrink-0" />;
    case "success":
      return <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />;
    default:
      return <Info className="w-4 h-4 text-sky-500 shrink-0" />;
  }
}

export const NotificationsBell = () => {
  const [open, setOpen] = useState(false);
  const { notifications, unreadCount, markAllAsRead, markAsRead, clearAll } =
    useNotifications();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-9 w-9 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground cursor-pointer transition-colors"
          title="Notificações"
          aria-label="Abrir notificações"
        >
          <Bell className="w-4 h-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] px-1 items-center justify-center rounded-full bg-blue-600 text-[10px] font-bold text-white shadow-xs animate-in zoom-in">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        side="right"
        sideOffset={12}
        className="w-80 sm:w-96 p-0 shadow-xl border border-border/80 bg-card rounded-xl overflow-hidden z-50"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border/60 bg-muted/30">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-sm">Notificações</span>
            {unreadCount > 0 && (
              <span className="px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400">
                {unreadCount} nova{unreadCount > 1 ? "s" : ""}
              </span>
            )}
          </div>

          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={markAllAsRead}
                className="h-7 px-2 text-[11px] font-medium text-muted-foreground hover:text-foreground cursor-pointer"
                title="Marcar todas como lidas"
              >
                <CheckCheck className="w-3.5 h-3.5 mr-1" />
                Lidas
              </Button>
            )}
            {notifications.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAll}
                className="h-7 px-2 text-[11px] font-medium text-muted-foreground hover:text-destructive cursor-pointer"
                title="Limpar todas as notificações"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" />
                Limpar
              </Button>
            )}
          </div>
        </div>

        {/* Notifications List */}
        <div className="max-h-[380px] overflow-y-auto divide-y divide-border/40">
          {notifications.length === 0 ? (
            <div className="py-12 px-6 text-center flex flex-col items-center justify-center gap-2 text-muted-foreground">
              <div className="w-10 h-10 rounded-full bg-muted/60 flex items-center justify-center">
                <Bell className="w-5 h-5 opacity-40" />
              </div>
              <p className="text-xs font-medium">Nenhuma notificação</p>
              <p className="text-[11px] text-muted-foreground/70 max-w-[220px]">
                Você está em dia com todas as atualizações de tarefas e agenda.
              </p>
            </div>
          ) : (
            notifications.map((item) => {
              const content = (
                <div
                  key={item.id}
                  onClick={() => {
                    if (!item.read) markAsRead(item.id);
                  }}
                  className={cn(
                    "flex items-start gap-3 p-3.5 transition-colors cursor-pointer hover:bg-muted/40 text-left w-full",
                    !item.read ? "bg-blue-500/[0.04]" : "opacity-85",
                  )}
                >
                  <div className="mt-0.5 rounded-full p-1.5 bg-muted/80">
                    {getNotificationIcon(item.type)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-1 mb-0.5">
                      <p
                        className={cn(
                          "text-xs truncate",
                          !item.read
                            ? "font-semibold text-foreground"
                            : "font-medium text-foreground/80",
                        )}
                      >
                        {item.title}
                      </p>
                      <span className="text-[10px] text-muted-foreground shrink-0 flex items-center gap-0.5">
                        <Clock className="w-2.5 h-2.5 inline" />
                        {formatNotificationTime(item.timestamp)}
                      </span>
                    </div>

                    <p className="text-[11px] text-muted-foreground line-clamp-2 leading-relaxed">
                      {item.description}
                    </p>
                  </div>

                  {!item.read && (
                    <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0 self-center" />
                  )}
                </div>
              );

              if (item.link) {
                return (
                  <Link
                    key={item.id}
                    to={item.link}
                    onClick={() => {
                      if (!item.read) markAsRead(item.id);
                      setOpen(false);
                    }}
                    className="block no-underline"
                  >
                    {content}
                  </Link>
                );
              }

              return content;
            })
          )}
        </div>

        {/* Footer info */}
        {notifications.length > 0 && (
          <div className="px-4 py-2 border-t border-border/40 bg-muted/20 text-center">
            <span className="text-[10px] text-muted-foreground flex items-center justify-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Sincronização em tempo real ativa
            </span>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
